import { Injectable } from "@angular/core";
import { ContentValue } from "@fabric-types/svc-content";
import { DataProtectionConfigService, FraudPreventionConfigService } from "@obg/ngx.common";
import { CoreHttpClient, CurrencyService, DepositLimitPeriod, Market, ProductType } from "@obg/ngx.core";
import { DocumentRef } from "@obg/ngx.global.evo";
import { EMPTY, Observable } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";

import { RegistrationConfigService } from "../registration.config";
import { RegistrationPayload, RegistrationResponse } from "../registration.model";
import { RegistrationService } from "../registration.service";

import {
	GetSPIDJwtTokenPayload,
	GetSPIDAuthorizationUrlPayload,
	SPIDRegistrationRequest,
	SPIDRegistrationPayload,
} from "./registration-with-spid.model";

@Injectable({
	providedIn: "root",
})
export class RegistrationWithSPIDService {
	constructor(
		private httpClient: CoreHttpClient,
		private registrationConfig: RegistrationConfigService,
		private registrationService: RegistrationService,
		private fraudPreventionConfigService: FraudPreventionConfigService,
		private dataProtectionConfig: DataProtectionConfigService,
		private currencyService: CurrencyService,
		private documentRef: DocumentRef
	) {}

	private countryCode$ = this.documentRef.obgEventBus
		? new Observable(this.documentRef.obgEventBus.retrieve(ContentValue.getDefaultCountryCode))
		: EMPTY;

	getSPIDAuthorizationUrl(
		{ flowType }: GetSPIDAuthorizationUrlPayload = { flowType: "registration" }
	): Observable<string> {
		const fallbackDomain = "https://www.test.starcasino.it";
		const originDomain = window.location.origin;
		const isDev = originDomain.includes(".dev");
		const isDemo = originDomain.includes(".demo");

		const returnUrlDomain = isDev || isDemo ? fallbackDomain : originDomain;
		const returnUrl = `${returnUrlDomain}/pagina-registrazione/spid/confirmation`;

		const apiUrl = this.registrationConfig.getSPIDAuthorizationUrl();
		const url = `${apiUrl}?flowType=${flowType}&returnUrl=${returnUrl}`;
		return this.countryCode$.pipe(
			take(1),
			switchMap((countryCode) =>
				this.httpClient.get<string>({
					url,
					headers: {
						"x-obg-country-code": countryCode,
						"x-obg-domain": isDev ? fallbackDomain : originDomain,
					},
				})
			)
		);
	}

	getSPIDJwtToken({ sessionId, sessionKey, transactionId }: GetSPIDJwtTokenPayload): Observable<string> {
		const apiUrl = this.registrationConfig.getSPIDJwtUrl();
		const url = `${apiUrl}?sessionId=${sessionId}&sessionKey=${sessionKey}&transactionId=${transactionId}`;

		return this.httpClient.get<string>({
			url,
		});
	}

	getRegistrationToken(payload: SPIDRegistrationPayload, market: Market) {
		return this.countryCode$.pipe(
			take(1),
			map((countryCode) => this.buildRegisterRequest(payload, market, countryCode)),
			switchMap((requestData) =>
				this.httpClient.post<RegistrationResponse>({
					url: this.registrationConfig.getRegisterEndpoint(),
					data: requestData,
				})
			)
		);
	}

	private buildRegisterRequest(
		payload: SPIDRegistrationPayload,
		market: Market,
		countryCode: string
	): SPIDRegistrationRequest {
		const {
			email,
			username,
			password,
			recoveryQuestion,
			recoveryAnswer,
			depositLimitAmount,
			brandMarketing,
			privacyPolicy,
			termsAndConditions,
			iovationBlackBox,
			spidJwtToken,
		} = payload;

		let consents = {};
		let marketingOffers = {};

		if (!this.dataProtectionConfig.getMarketingConsentDisabled() && brandMarketing) {
			consents = {
				brandMarketing,
				...(this.dataProtectionConfig.getGroupMarketingDisabled() && {
					groupMarketing: false,
					thirdParty: true,
				}),
			};

			marketingOffers = {
				acceptEmailOffers: brandMarketing,
				acceptSmsOffers: brandMarketing,
				acceptTelesalesOffers: brandMarketing,
			};
		}

		return {
			language: market.dataLanguageCode ?? market.languageCode,
			timeZone: "utc",
			affiliateMetadata: this.registrationService.getAffiliateMetaData(),
			credential: {
				username,
				email,
				password,
			},
			currency: this.currencyService.getCurrencyCodeByCountryCode(countryCode),
			depositLimit: {
				amount: depositLimitAmount || DepositLimitPeriod.NoLimit,
				depositLimitPeriod: DepositLimitPeriod.NoLimit,
			},
			subscriptions: {
				...marketingOffers,
				email,
			},
			iovationBlackBox: this.fraudPreventionConfigService.getEnabledInRegistration() ? iovationBlackBox : "",
			legal: {
				termsAndConditions,
				privacyPolicy,
				contractGame: privacyPolicy,
			},
			productMetadata: {
				registeredFromProduct: ProductType.Common,
			},
			recoveryQuestion: {
				question: recoveryQuestion,
				answer: recoveryAnswer,
			},
			providerVerificationDetails: {
				providerName: "Techsson.Authentication.Oidc.Host",
				tokens: [
					{
						action: "authentication",
						value: spidJwtToken,
					},
				],
			},

			// Extra fields
			consents,
		};
	}

	mapToRegistrationPayload(payload: SPIDRegistrationPayload): RegistrationPayload {
		// If we need more info, we can destruct payload.spidJwtToken, and add data from token here
		return {
			credential: {
				email: payload.email,
				password: payload.password,
				username: payload.username,
			},
			iovationBlackBox: payload.iovationBlackBox,
			consents: {
				brandMarketing: payload.brandMarketing,
			},
		} as unknown as RegistrationPayload;
	}
}
